import { QueryKey, useInfiniteQuery, UseInfiniteQueryOptions } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import logger from 'src/utils/logger';
import { ASSIGN_TAKE_ITEMS } from '../../constants';

type FetchFunction<T> = (args: {
  pageParam: number;
}) => Promise<{ items: T[]; nextSkip?: number | null }>;

type UseInfiniteListOptions<T> = {
  queryKey: QueryKey;
  queryFn: FetchFunction<T>;
  take?: number;
} & Partial<UseInfiniteQueryOptions<{ items?: T[]; nextSkip: number | null }>>;

export const useInfiniteList = <T>({
  queryKey,
  queryFn,
  take = ASSIGN_TAKE_ITEMS,
  ...options
}: UseInfiniteListOptions<T>) => {
  const {
    data: tempData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    ...queryResult
  } = useInfiniteQuery({
    queryKey,
    queryFn,
    getNextPageParam: (lastPage) =>
      lastPage.items?.length === 0 || (lastPage.items?.length ?? 0) < take
        ? null
        : lastPage.nextSkip,
    ...options,
  });

  const observerRef = useRef<HTMLLIElement | null>(null);

  // Store observer instance in a ref to maintain it between renders
  const observerInstanceRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Only create new observer if one doesn't exist already
    if (!observerInstanceRef.current && observerRef.current) {
      observerInstanceRef.current = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            hasNextPage &&
            !isFetchingNextPage &&
            options.enabled !== false
          ) {
            fetchNextPage();
          }
        },
        { threshold: 0.5 },
      );

      if (observerRef.current) {
        observerInstanceRef.current.observe(observerRef.current);
      }
    }

    // If observer exists but target element changed, reconnect it
    if (observerInstanceRef.current && observerRef.current) {
      // Ensure we're observing the current ref
      observerInstanceRef.current.disconnect();
      observerInstanceRef.current.observe(observerRef.current);
    }

    return () => {
      // Don't disconnect the observer during normal renders
      // Only clean up if component is unmounting (not on every filter change)
      if (options.enabled === false) {
        logger.info('IntersectionObserver temporarily disabled, not disconnecting');
      }
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, options.enabled]);

  // Add cleanup effect that only runs on unmount
  useEffect(
    () => () => {
      // This will only run when component unmounts
      if (observerInstanceRef.current) {
        observerInstanceRef.current.disconnect();
        observerInstanceRef.current = null;
      }
    },
    [],
  );

  const data = useMemo(
    () => tempData?.pages?.flatMap((page) => page.items) ?? [],
    [tempData?.pages],
  );

  return { data, fetchNextPage, hasNextPage, isFetchingNextPage, observerRef, ...queryResult };
};
